import { useState, useEffect, useCallback } from "react";
import type { Blog, UpdateBlogParams } from "../types/blog";
import { blogService } from "../services/blogService";

export function useBlogs(status?: "private" | "draft" | "public") {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await blogService.getAll({ status });
            setBlogs(data);
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to fetch blogs"));
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        fetchBlogs();
    }, [fetchBlogs, status]);

    const createBlog = async (b: Blog) => {
        try {
            const newBlog = await blogService.create(b);
            await fetchBlogs();
            return newBlog;
        } catch (err) {
            throw err;
        }
    };

    const updateBlog = async (id: string | number, b: UpdateBlogParams) => {
        try {
            const updatedBlog = await blogService.update(id, b);
            await fetchBlogs();
            return updatedBlog;
        } catch (err) {
            throw err;
        }
    };

    const updateBlogContent = async (id: string | number, content: string) => {
        try {
            const updatedBlog = await blogService.updateContent(id, content);
            await fetchBlogs();
            return updatedBlog;
        } catch (err) {
            throw err;
        }
    };

    return { blogs, loading, error, refresh: fetchBlogs, createBlog, updateBlog, updateBlogContent };
}
